import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle, Play, Menu, X, Building2 } from 'lucide-react';
import FooterModern from '../common/FooterModern';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';
import { useAuth } from '../../hooks/useAuth';
import { signInWithRedirect, signIn, resendSignUpCode, confirmSignUp, getCurrentUser } from 'aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import {
  clearConflictingAuthSessionForInvite,
  inviteEmailFromRecord,
  inviteRegistrationAutofillProps,
  resetInviteLandingStorage,
  storeInviteLandingContext,
} from '../../lib/orgInviteLanding';

function OrganizationsLanding() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [invitation, setInvitation] = useState<any>(null);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    organizationName: '',
    role: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);

  const inviteEmail = inviteEmailFromRecord(invitation?.email);
  const registrationEmail = inviteEmail || inviteEmailFromRecord(form.email);

  // Google sign-in handler
  const handleGoogleSignIn = async () => {
    try {
      setError(null);
      
      const token = searchParams.get('token');
      if (token) {
        storeInviteLandingContext(token, 'org-referral');
      }
      
      await signInWithRedirect({ provider: 'Google', customState: 'prompt=select_account' });
    } catch (e) {
      const err = e as { message?: string };
      setError(err?.message || 'Google sign-in failed.');
    }
  };

  // Fetch invitation details when component mounts
  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) return;

    resetInviteLandingStorage();
    storeInviteLandingContext(token, 'org-referral');
    fetchInvitationDetails(token);
    setShowForm(true);
  }, [searchParams]);

  const fetchInvitationDetails = async (token: string) => {
    setInvitationLoading(true);
    setInvitationError(null);
    
    try {
      const client = generateClient<Schema>();
      const invitations = await client.models.UserInvitation.list({
        authMode: 'apiKey',
        filter: {
          token: { eq: token }
        }
      });

      if (invitations.data && invitations.data.length > 0) {
        const invitationData = invitations.data[0];
        if (invitationData.status === 'REGISTERED') {
          setInvitationError('This invitation link has already been used.');
          setInvitation(null);
          return;
        }

        setInvitation(invitationData);
        const invitedEmail = inviteEmailFromRecord(invitationData.email);
        await clearConflictingAuthSessionForInvite(invitedEmail);
        
        // Replace form state — never merge with a cached email from a prior visit.
        setForm({
          firstName: '',
          lastName: '',
          email: invitedEmail,
          password: '',
          confirmPassword: '',
          phoneNumber: '',
          organizationName: invitationData.organizationName || '',
          role: invitationData.inviteeRole || '',
        });
      } else {
        setInvitationError('Invalid or expired invitation link.');
      }
    } catch (err) {
      // Error fetching invitation logged
      setInvitationError('Error loading invitation details.');
    } finally {
      setInvitationLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const token = searchParams.get('token');
      if (token && invitation) {
        storeInviteLandingContext(token, 'org-referral');
      }
      
      // STEP 1: Register (creates Cognito user + User record in database)
      await register(
        registrationEmail,
        form.password,
        form.firstName,
        form.lastName,
        form.phoneNumber, // optional
        invitation?.organizationId, // Pass organizationId for invited users
        undefined, // kidProfileId (not applicable for therapist/caregiver)
        invitation?.inviteeRole // Pass role from invitation ('THERAPIST')
      );
      
      // STEP 2: Automatically sign in the user after registration
      // signUp() doesn't automatically sign the user in, so we need to do it explicitly
      try {
        const { nextStep } = await signIn({
          username: registrationEmail,
          password: form.password,
          options: {
            authFlowType: "USER_SRP_AUTH"
          }
        });

        if (nextStep.signInStep === 'DONE') {
          // User is now authenticated!
          
          // STEP 3: Update invitation status to REGISTERED
          if (invitation?.id) {
            try {
              const client = generateClient<Schema>();
              await client.models.UserInvitation.update({
                id: invitation.id,
                status: 'REGISTERED'
              });
            } catch (err) {
              console.error('Error updating invitation status:', err);
              // Don't fail the flow if invitation update fails
            }
          }
          
          // STEP 4: Redirect based on invitation role
          navigate(getPostConfirmRedirect());
        } else if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
          // Store registration info in localStorage in case user navigates away
          localStorage.setItem('pendingVerification', JSON.stringify({
            email: registrationEmail,
            password: form.password,
            invitationToken: searchParams.get('token'),
            timestamp: Date.now()
          }));
          setNeedsConfirmation(true);
          setError('Please check your email for the verification code and enter it below.');
          // Don't navigate - stay on this page to show verification modal
        } else {
          // If sign-in requires additional steps (like MFA)
          setError('Registration successful. Please sign in to complete additional authentication.');
        }
      } catch (signInError: any) {
        if (signInError.name === 'UserAlreadyAuthenticatedException') {
          if (invitation?.id) {
            try {
              const client = generateClient<Schema>();
              await client.models.UserInvitation.update({
                id: invitation.id,
                status: 'REGISTERED'
              });
            } catch (err) {
              // Non-critical
            }
          }
          window.location.href = getPostConfirmRedirect();
          return;
        }
        
        if (signInError.name === 'UserNotConfirmedException') {
          setNeedsConfirmation(true);
          setError('Please check your email for the verification code and enter it below.');
        } else {
          setError('Registration successful! Please sign in manually to continue.');
        }
      }
    } catch (err: any) {
      const message = err?.message || '';
      const isUserExists =
        err?.name === 'UsernameExistsException' ||
        err?.name === 'UserAlreadyExistsException' ||
        message.toLowerCase().includes('user already exists');

      if (isUserExists) {
        // Account already exists - try to sign in
        try {
          try {
            await getCurrentUser();
            // Already authenticated, app-level auth listener will handle redirect
            setError(null);
            return;
          } catch {
            // Not authenticated, proceed with sign-in
          }
          
          const { nextStep } = await signIn({
            username: registrationEmail,
            password: form.password,
            options: { authFlowType: "USER_SRP_AUTH" }
          });

          if (nextStep.signInStep === 'DONE') {
            setError(null);
            return; // App-level auth listener will handle redirect
          }

          if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
            setNeedsConfirmation(true);
            setError('Account exists but is not confirmed. Please enter the verification code sent to your email.');
          } else {
            setError('Account already exists. Please sign in to complete additional authentication.');
          }
        } catch (signInError: any) {
          if (signInError?.name === 'UserAlreadyAuthenticatedException') {
            setError(null);
            return; // App-level auth listener will handle redirect
          }
          
          if (signInError?.name === 'UserNotConfirmedException') {
            try {
              await resendSignUpCode({ username: registrationEmail });
              setNeedsConfirmation(true);
              setError('Account exists but is not confirmed. We resent the confirmation code to your email.');
            } catch {
              setNeedsConfirmation(true);
              setError('Account exists but is not confirmed. Please check your email for the confirmation code.');
            }
          } else {
            setError('Account already exists. Please sign in to accept the invitation.');
          }
        }
        return;
      }

      setError(message || 'Error creating account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoRequest = (e: React.FormEvent) => {
    e.preventDefault();
    // Demo request submitted logged
  };

  const getPostConfirmRedirect = () => {
    if (invitation?.inviteeRole === 'ORG_ADMIN') {
      return '/org-admin-dashboard';
    }
    if (invitation?.inviteeRole === 'THERAPIST' || invitation?.inviteeRole === 'CLINICIAN') {
      return '/org-admin-dashboard';
    }
    if (invitation?.inviteeRole === 'PARENT' && invitation?.kidProfileId) {
      return `/kid-profile/${invitation.kidProfileId}`;
    }
    return '/org-admin-dashboard';
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!confirmationCode.trim()) {
      setError('Please enter the verification code.');
      return;
    }
    setIsConfirming(true);
    try {
      await confirmSignUp({
        username: registrationEmail,
        confirmationCode: confirmationCode.trim()
      });
      
      // Check if user is already authenticated after confirmation (confirmSignUp may auto-sign-in)
      let user = null;
      try {
        user = await getCurrentUser();
        console.log('✅ User already authenticated after confirmation - skipping sign-in');
      } catch {
        // Not authenticated, need to sign in
        console.log('⚠️ User not authenticated after confirmation - signing in...');
        try {
          const { nextStep } = await signIn({
            username: registrationEmail,
            password: form.password,
            options: { authFlowType: "USER_SRP_AUTH" }
          });
          
          if (nextStep.signInStep === 'DONE') {
            user = await getCurrentUser();
            console.log('✅ User signed in successfully');
          } else {
            localStorage.setItem('redirectAfterLogin', getPostConfirmRedirect());
            setError('Verification succeeded. Please sign in to complete additional authentication.');
            setIsConfirming(false);
            return;
          }
        } catch (signInError: any) {
          if (signInError?.name === 'UserAlreadyAuthenticatedException') {
            // User got authenticated somehow, get the user
            user = await getCurrentUser();
            console.log('✅ User authenticated (exception caught)');
          } else {
            setError('Verification succeeded but sign-in failed. Please try signing in manually.');
            setIsConfirming(false);
            return;
          }
        }
      }
      
      // User is authenticated - trigger auth event and redirect
      if (user) {
        Hub.dispatch('auth', { event: 'signIn', data: { user } });
        
        // Update invitation status
        if (invitation?.id) {
          try {
            const client = generateClient<Schema>();
            await client.models.UserInvitation.update({
              id: invitation.id,
              status: 'REGISTERED'
            });
          } catch (err) {
            // Non-critical
          }
        }
        
        setNeedsConfirmation(false);
        setError(null);
        console.log('🚀 Redirecting to:', getPostConfirmRedirect());
        window.location.href = getPostConfirmRedirect();
      }
    } catch (confirmError: any) {
      if (confirmError?.name === 'CodeMismatchException') {
        setError('Invalid verification code. Please try again.');
      } else if (confirmError?.name === 'ExpiredCodeException') {
        setError('Verification code expired. Please request a new code.');
      } else if (confirmError?.name === 'UserAlreadyAuthenticatedException') {
        // User is already authenticated, just navigate
        setNeedsConfirmation(false);
        setError(null);
        navigate(getPostConfirmRedirect());
      } else {
        setError('Unable to verify the code. Please try again.');
      }
    } finally {
      setIsConfirming(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    try {
      await resendSignUpCode({ username: registrationEmail });
      setError('We resent the verification code to your email.');
    } catch {
      setError('Unable to resend the verification code. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50 backdrop-blur-sm bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">{/* Removed duplicate TinyWins logo and heart icon */}</div>
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#how-it-works" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">
                How It Works
              </a>
              <a href="#benefits" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">
                Benefits
              </a>
              <a href="#case-study" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">
                Case Studies
              </a>
              <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium">
                Request a Demo
              </button>
            </nav>
            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-100">
              <div className="flex flex-col space-y-4">
                <a href="#how-it-works" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">
                  How It Works
                </a>
                <a href="#benefits" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">
                  Benefits
                </a>
                <a href="#case-study" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">
                  Case Studies
                </a>
                <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium w-fit">
                  Request a Demo
                </button>
              </div>
            </div>
          )}
        </div>
      </header>
      {/* Registration Form Section (for invited users) */}
      {showForm && (
        <section id="registration" className="py-16 lg:py-20 bg-gradient-to-br from-blue-50 to-purple-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl">
              <div className="text-center mb-10">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                  Complete Your Registration
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Join your organization on TinyWins and start making a difference in children's lives.
                </p>
              </div>

              {/* Invitation Notice */}
              {invitation && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-800">
                        <strong>You've been invited!</strong> You're joining <strong>{invitation.organizationName}</strong> as a <strong>{invitation.inviteeRole}</strong>.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Invitation Error */}
              {invitationError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <X className="h-5 w-5 text-red-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{invitationError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {invitationLoading && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-800">Loading invitation details...</p>
                    </div>
                  </div>
                </div>
              )}

              {(
                <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        value={form.firstName}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder="Enter your first name"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        value={form.lastName}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder="Enter your last name"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="invite-email"
                      key={registrationEmail || 'invite-email-empty'}
                      value={registrationEmail}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Enter your email"
                      required
                      readOnly={!!inviteEmail}
                      {...inviteRegistrationAutofillProps}
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                      Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      autoComplete="new-password"
                      value={form.password}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Create a password"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      autoComplete="new-password"
                      value={form.confirmPassword}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Confirm your password"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="phoneNumber" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number (optional)
                    </label>
                    <input
                      type="tel"
                      id="phoneNumber"
                      value={form.phoneNumber}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="1234567890"
                      pattern="[0-9]{10}"
                      maxLength={10}
                    />
                  </div>
                  {error && <div className="text-red-600 text-center font-semibold text-sm mb-2">{error}</div>}
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-8 rounded-lg text-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
                    disabled={loading}
                  >
                    {loading ? 'Creating Account...' : 'Complete Registration'}
                    <ArrowRight className="inline-block ml-2 h-5 w-5" />
                  </button>
                  
                  {/* Google Sign-in Button */}
                  <div className="text-center my-4">
                    <span className="text-gray-500 text-sm">or</span>
                  </div>
                  
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    className="w-full bg-white border-2 border-gray-300 text-gray-700 py-4 px-8 rounded-lg text-lg font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
                    disabled={loading}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </button>
                  
                  <p className="text-center text-sm text-gray-500 mt-4">
                    Free to get started • No credit card required • No waitlist
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>
      )}

      {needsConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-xl font-semibold text-gray-900 text-center">Verify Your Email</h3>
            <p className="mt-2 text-sm text-gray-600 text-center">
              Enter the verification code sent to <strong>{registrationEmail}</strong>.
            </p>
            <form onSubmit={handleConfirm} className="mt-6 space-y-4">
              <div>
                <label htmlFor="confirmationCode" className="block text-sm font-semibold text-gray-700 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  id="confirmationCode"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="Enter code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                />
              </div>
              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg text-base font-semibold hover:bg-blue-700 transition-colors"
                disabled={isConfirming}
              >
                {isConfirming ? 'Verifying...' : 'Verify & Continue'}
              </button>
              <button
                type="button"
                onClick={handleResendCode}
                className="w-full text-sm text-blue-600 hover:text-blue-700"
              >
                Resend verification code
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Building2 className="h-4 w-4 mr-2" />
                Your privacy is important to us
              </div>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Empower Your Team.
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Support Every Child.</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Smart developmental tracking for autism clinics, schools, and therapy centers. 
                With TinyWins, you can manage therapists, track progress, and elevate care — all from one secure platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl">
                  Request a Demo
                  <ArrowRight className="inline-block ml-2 h-5 w-5" />
                </button>
                <button className="flex items-center justify-center border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-colors">
                  <Play className="h-5 w-5 mr-2" />
                  Watch Overview
                </button>
              </div>
              <div className="flex items-center space-x-6 text-sm text-gray-500">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  COPPA compliant
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  No waitlist
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  24/7 support
                </div>
              </div>
            </div>
            <div className="relative">
              {/* Image removed as requested */}
            </div>
          </div>
        </div>
      </section>
      {/* ... rest of the page ... */}
      {/* Demo Request Section */}
      <section id="demo-request" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-8 lg:p-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Ready to Transform Your Organization?
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                See how TinyWins can streamline your developmental tracking and improve outcomes 
                for every child in your care.
              </p>
            </div>
            <form onSubmit={handleDemoRequest} className="bg-white rounded-2xl p-8 shadow-lg">
              {/* ... form fields ... */}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-8 rounded-lg text-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
              >
                Request a Demo
                <ArrowRight className="inline-block ml-2 h-5 w-5" />
              </button>
              <p className="text-center text-sm text-gray-500 mt-4">
                Free demo • No commitment required • No waitlist
              </p>
            </form>
          </div>
        </div>
      </section>
      {/* Footer */}
      <FooterModern />
    </div>
  );
}

export default OrganizationsLanding; 